/**
 * /workspace/sell/returns — RMA queue over shop_returns.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  RotateCcw, CheckCircle2, XCircle, Truck, PackageOpen, Loader2, IndianRupee, Filter,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "requested", "approved", "in_transit", "received", "refunded", "denied"];
const NEXT: Record<string, string | null> = {
  requested: "approved", approved: "in_transit", in_transit: "received", received: "refunded",
};

interface RMA {
  rma_id: string;
  order_id: string;
  buyer_name?: string;
  buyer_email?: string;
  product_title?: string;
  quantity: number;
  refund_amount: number;
  reason: string;
  status: string;
  created_at: string;
  refunded_at?: string;
}

export default function SellReturns() {
  const [status, setStatus] = React.useState("all");
  const qc = useQueryClient();

  const q = useQuery<{ items: RMA[]; kpi: any }>({
    queryKey: ["sell-returns", status],
    queryFn: async () => (await api.get(`/workspace/sell/returns?status=${status}&limit=100`)).data,
  });

  const decide = useMutation({
    mutationFn: async ({ rma_id, status }: { rma_id: string; status: string }) => (await api.post(`/workspace/sell/returns/${rma_id}/decide`, { status })).data,
    onSuccess: () => { toast.success("Return updated."); qc.invalidateQueries({ queryKey: ["sell-returns"] }); },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Failed"),
  });

  const k = q.data?.kpi;
  return (
    <DashboardLayout title="Returns" subtitle="RMA queue — approve, dispatch, receive, refund. Every action is audited.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Requested"   value={k?.by_status?.requested ?? 0} icon={PackageOpen} tone="amber"/>
        <Kpi label="In transit"  value={k?.by_status?.in_transit ?? 0} icon={Truck}      tone="blue"/>
        <Kpi label="Refunded"    value={k?.by_status?.refunded ?? 0}  icon={CheckCircle2} tone="emerald"/>
        <Kpi label="Refund total" value={inr(k?.refunded_amount)}     icon={IndianRupee} tone="fuchsia"/>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
        {STATUSES.map((s) => (
          <button key={s}
            data-testid={`returns-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`h-8 px-3 rounded-full text-xs font-semibold capitalize border ${
              status === s
                ? "bg-[color:var(--sb-navy,#0A1128)] text-white border-transparent"
                : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"
            }`}
          >
            {s.replace("_", " ")}
            {k?.by_status?.[s] ? <span className="ml-1 text-[10px] opacity-70">·{k.by_status[s]}</span> : null}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div className="p-8 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/> Loading…</div>
      ) : (q.data?.items?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--sg-border)] p-10 text-center text-[color:var(--sg-muted)]">
          <RotateCcw className="h-8 w-8 mx-auto mb-2 text-emerald-500"/>
          No returns in this bucket. Sellers are happy.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {q.data!.items.map((r) => {
            const next = NEXT[r.status];
            const busy = decide.isPending && decide.variables?.rma_id === r.rma_id;
            return (
              <div key={r.rma_id} data-testid={`return-card-${r.rma_id}`} className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[color:var(--sg-muted-bg)]">{r.rma_id}</span>
                    <StatusBadge status={r.status}/>
                  </div>
                  <span className="text-[10px] text-[color:var(--sg-muted)]">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="font-serif-display text-base font-bold text-[color:var(--sg-fg)] truncate">{r.product_title || r.order_id}</div>
                <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
                  {r.buyer_name || "Buyer"} · {r.buyer_email || "—"} · Qty {r.quantity} · Refund <b className="text-[color:var(--sg-fg)]">{inr(r.refund_amount)}</b>
                </div>
                <p className="mt-2 text-sm text-[color:var(--sg-fg)] leading-relaxed">“{r.reason}”</p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {next && (
                    <Button
                      data-testid={`return-advance-${r.rma_id}`}
                      size="sm"
                      className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={busy}
                      onClick={() => decide.mutate({ rma_id: r.rma_id, status: next })}
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : `→ ${next.replace("_", " ")}`}
                    </Button>
                  )}
                  {r.status === "requested" && (
                    <Button
                      data-testid={`return-deny-${r.rma_id}`}
                      size="sm" variant="outline"
                      className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300"
                      disabled={busy}
                      onClick={() => decide.mutate({ rma_id: r.rma_id, status: "denied" })}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1"/> Deny
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

function inr(n?: number | null) {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function Kpi({ label, value, icon: Icon, tone }: { label: string; value: any; icon: any; tone: string }) {
  const t: Record<string, string> = {
    blue:    "text-blue-600 dark:text-blue-300 from-blue-500/15",
    amber:   "text-amber-600 dark:text-amber-300 from-amber-500/15",
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300 from-fuchsia-500/15",
  };
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${t[tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${t[tone].split(" ")[0]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {label}
        </div>
        <div className="mt-1.5 font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, string> = {
    requested:  "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    approved:   "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200",
    in_transit: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200",
    received:   "bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-200",
    refunded:   "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    denied:     "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m[status] || m.requested}`}>{status.replace("_", " ")}</span>;
}
